"use client";

import { ArrowLeft } from "lucide-react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AeraButton } from "@/app/components/ui/AeraButton";
import { useBackToHomeHref } from "@/hooks/useBackToHomeHref";
import { AERA_PAGE_CARD_CLASS } from "@/app/components/layout/aeraPageLayout";

export { AERA_PAGE_CARD_CLASS };

const MAX_WIDTH_CLASS = {
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "6xl": "max-w-6xl",
  full: "w-full max-w-none px-3! md:px-4!",
} as const;

export type AeraPageMaxWidth = keyof typeof MAX_WIDTH_CLASS;

const PAGE_GRADIENT =
  "pointer-events-none absolute inset-0 bg-[radial-gradient(1200px_700px_at_50%_-160px,rgba(245,158,11,0.22),transparent_60%),radial-gradient(900px_520px_at_90%_30%,rgba(252,211,77,0.20),transparent_55%),radial-gradient(860px_520px_at_10%_60%,rgba(254,243,199,0.55),transparent_60%)]";

export type AeraPageShellProps = {
  title: string;
  subtitle?: string;
  backHref?: string;
  backLabel?: string;
  headerAction?: ReactNode;
  maxWidth?: AeraPageMaxWidth;
  mainClassName?: string;
  /** When true, children render full-width below the header (no max-width main padding). */
  fullWidthBody?: boolean;
  children: ReactNode;
};

export function AeraPageShell({
  title,
  subtitle,
  backHref,
  backLabel = "กลับหน้าหลัก",
  headerAction,
  maxWidth = "6xl",
  mainClassName = "",
  fullWidthBody = false,
  children,
}: AeraPageShellProps) {
  const defaultBackHref = useBackToHomeHref();
  const resolvedBackHref = backHref ?? defaultBackHref;
  const widthClass = MAX_WIDTH_CLASS[maxWidth];
  const headerWidthClass = fullWidthBody ? MAX_WIDTH_CLASS.full : widthClass;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  const syncScrollShadow = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;
    setIsScrolled(container.scrollTop > 0);
  }, []);

  useEffect(() => {
    syncScrollShadow();
  }, [syncScrollShadow]);

  return (
    <div className="relative isolate grid h-dvh grid-rows-[auto_minmax(0,1fr)] overflow-hidden bg-aera-offwhite">
      <div aria-hidden className={PAGE_GRADIENT} />

      <header
        className={`relative z-50 bg-aera-white/90 backdrop-blur-md transition-shadow duration-200 ${
          isScrolled ? "shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08)]" : ""
        }`}
      >
        <div
          className={`mx-auto flex ${headerWidthClass} items-center gap-4 py-3 px-4 md:px-6`}
        >
          <AeraButton
            href={resolvedBackHref}
            variant="outline"
            size="sm"
            icon={<ArrowLeft className="h-4 w-4" aria-hidden />}
            className="shrink-0"
          >
            <span className="md:hidden">กลับ</span>
            <span className="hidden md:inline">{backLabel}</span>
          </AeraButton>
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <div className="min-w-0">
              <h1 className="truncate text-base font-bold text-aera-charcoal md:text-lg">
                {title}
              </h1>
              {subtitle ? (
                <p className="truncate text-[11px] text-aera-gray md:text-xs">
                  {subtitle}
                </p>
              ) : null}
            </div>
          </div>
          {headerAction ? <div className="shrink-0">{headerAction}</div> : null}
        </div>
      </header>

      <div
        ref={scrollRef}
        onScroll={syncScrollShadow}
        className={`relative min-h-0 overflow-y-auto overflow-x-hidden overscroll-y-contain ${
          fullWidthBody ? `flex flex-col ${mainClassName}`.trim() : ""
        }`}
      >
        {fullWidthBody ? (
          children
        ) : (
          <main
            className={`mx-auto w-full ${widthClass} space-y-4 px-4 py-8 md:space-y-6 md:px-6 md:py-10 ${mainClassName}`.trim()}
          >
            {children}
          </main>
        )}
      </div>
    </div>
  );
}
